// [NEW FILE] Requirement 7: Appraisal (frontend only; the backend is NOT changed).
//
// In the backend an appraisal is a performance record (performance_record) inside a performance cycle
// (app/api/cycles.py): HR maps employees into a cycle, sets eligibility, reassigns the reviewer, launches the cycle
// and can close it only when every eligible appraisal is released (or acknowledged when the cycle requires it).
// Each appraisal moves Goal setting -> Goal approval -> Tracking -> Self assessment -> Manager assessment ->
// HR finalisation -> Released (app/api/reviews.py finalises and releases the result).
//
// This file builds the Appraisal page from the EXISTING endpoints only:
//   GET /performance-cycles                    cycle status, stage windows, workflow (release mode, acknowledgment)
//   GET /records?cycle_id=                     the appraisals the user may see (HR all, manager team, employee own)
//   GET /reports/employee-performance          names, department, designation, scores (HR / managers)
//   GET /records/{id}?include=results          the finalised result (rating) when it is not in the report yet
//   GET /configuration/rating_scheme           the PIP rule (lowest N bands) of the cycle's rating scheme (HR / Admin)
//   GET /pips                                  open improvement plans (HR all, managers the plans they review)
//
// Decision for each appraisal:
//   APPRAISAL        finalised, rating NOT in the PIP bands and no open improvement plan -> applicable for appraisal
//   PIP              rating in the PIP bands (Scoring & ratings -> Automatic improvement plan) or an open plan
//   AWAITING_RATING  HR has not finalised the rating yet
//   NOT_RATED        finalised without a rating (no valid performance data)
//   NOT_ELIGIBLE     excluded from this cycle
//   AWAITING_RELEASE (employee only) finalised but not released to the employee yet
import api from "./api";
import {
  ok, getMe, can, ORG_SCOPES, listRecords, cycleById, recordDetail, recordSection, ratingConfigFor, rememberName, nameOf, pMapSettled,
  resolveActorNames,
} from "./core";
import { getPipBands } from "../utils/ratingScale";
import { pipLabels } from "./autoPipApi";

export const APPRAISAL_DECISION = {
  APPRAISAL: "APPRAISAL",
  PIP: "PIP",
  AWAITING_RATING: "AWAITING_RATING",
  NOT_RATED: "NOT_RATED",
  NOT_ELIGIBLE: "NOT_ELIGIBLE",
  AWAITING_RELEASE: "AWAITING_RELEASE",
};

export const DECISION_LABEL = {
  APPRAISAL: "Applicable for appraisal",
  PIP: "Moved to PIP",
  AWAITING_RATING: "Awaiting final rating",
  NOT_RATED: "Not rated",
  NOT_ELIGIBLE: "Not eligible this cycle",
  AWAITING_RELEASE: "Result not released yet",
};

export const DECISION_TONE = {
  APPRAISAL: "success",
  PIP: "danger",
  AWAITING_RATING: "warning",
  NOT_RATED: "neutral",
  NOT_ELIGIBLE: "neutral",
  AWAITING_RELEASE: "info",
};

const RESULT_LABEL = {
  FINALIZED: "Finalised", RELEASED: "Released", ACKNOWLEDGED: "Acknowledged", CLOSED: "Closed",
  HR_REVIEW: "With HR", MANAGER_SUBMITTED: "Manager submitted", SELF_SUBMITTED: "Self submitted",
  IN_PROGRESS: "In progress", NOT_STARTED: "Not started",
};

// Appraisal stages of a performance record (backend performance_record.current_stage).
export const STAGE_LABEL = {
  GOAL_SETTING: "Goal setting", GOAL_APPROVAL: "Goal approval", TRACKING: "Tracking",
  SELF_ASSESSMENT: "Self assessment", MANAGER_ASSESSMENT: "Manager assessment", HR_FINALIZATION: "HR finalisation",
  RELEASED: "Released",
};
export const CYCLE_STATUS_LABEL = {
  DRAFT: "Draft", OPEN: "Open", REVIEW_IN_PROGRESS: "Review in progress", FINALIZATION: "Finalisation",
  CLOSED: "Closed", CANCELLED: "Cancelled",
};

const FINAL = ["FINALIZED", "RELEASED", "ACKNOWLEDGED", "CLOSED"];
const RELEASED = ["RELEASED", "ACKNOWLEDGED", "CLOSED"];
const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

export function decide({ eligibility, recordStatus, rating, notRated, pipStatus }, pipRatings, own) {
  const D = APPRAISAL_DECISION;
  if (eligibility && eligibility !== "ELIGIBLE") return D.NOT_ELIGIBLE;
  if (!FINAL.includes(recordStatus)) return D.AWAITING_RATING;
  if (own && !RELEASED.includes(recordStatus)) return D.AWAITING_RELEASE;
  if (!rating || notRated) return D.NOT_RATED;
  const openPlan = own ? pipStatus === "ACTIVE" : ["DRAFT", "ACTIVE"].includes(pipStatus);
  if (pipRatings.includes(rating) || openPlan) return D.PIP;
  return D.APPRAISAL;
}

export async function getAppraisals(cycleId) {
  const [me, cycle] = await Promise.all([getMe(), cycleById(cycleId)]);
  const hr = can(me, "performance.org.read", ORG_SCOPES);
  const records = await listRecords(cycleId);

  // PIP rule: the rating labels of the lowest N bands of this cycle's rating scheme. Only HR / Admin can read the
  // rating scheme; for managers and employees the decision uses the open improvement plans only.
  let pipRatings = [];
  let ruleKnown = false;
  let bandsCount = 0;
  try {
    const config = await ratingConfigFor(cycle);
    if (config.scheme && !config.restricted) {
      ruleKnown = true;
      bandsCount = getPipBands();
      pipRatings = pipLabels(config.bands || [], bandsCount);
    }
  } catch { /* no permission: fall back to open plans */ }

  // Names, departments and scores in one call (HR and managers); employees fall back to their record.
  const report = await api.get("/reports/employee-performance", { params: { cycle_id: cycleId, limit: 500 } })
    .then((r) => Object.fromEntries((r.data.rows || []).map((x) => [x.employee_id, x])))
    .catch(() => ({}));
  const plans = await api.get("/pips").then((r) => r.data || []).catch(() => []);
  const openPlan = {};
  plans.filter((p) => ["DRAFT", "ACTIVE"].includes(p.status))
    .forEach((p) => { if (!openPlan[p.employee_id] || p.pip_plan_id > openPlan[p.employee_id].pip_plan_id) openPlan[p.employee_id] = p; });

  // reporting managers' names (one lookup per unknown manager)
  await resolveActorNames([], records.map((r) => r.review_manager_employee_id)).catch(() => {});
  const rows = (await pMapSettled(records, async (rec) => {
    const own = rec.employee_id === me.employee_id;
    let info = report[rec.employee_id];
    if (!info) {
      const detail = await recordDetail(rec.performance_record_id).catch(() => null);
      const snap = detail?.context_snapshot || {};
      info = { employee_name: snap.employee_name, department_id: snap.department_id, department_name: snap.department_name, designation_name: snap.designation_name };
    }
    let rating = info.rating || null;
    let goal = num(info.goal_score);
    let kra = num(info.kra_score);
    let final = num(info.final_score);
    let notRated = false;
    let releasedAt = null;
    // The report shows the rating only after release; read the finalised result for the others.
    if (FINAL.includes(rec.record_status) && (!rating || own)) {
      const results = await recordSection(rec.performance_record_id, "results", []);
      const latest = results[0];
      if (latest) {
        rating = latest.rating_label_snapshot || null;
        notRated = latest.outcome === "NOT_RATED";
        goal = num(latest.goal_score); kra = num(latest.kra_score); final = num(latest.final_score);
        releasedAt = latest.released_at || null;
      }
    }
    const plan = openPlan[rec.employee_id] || null;
    const base = { eligibility: rec.eligibility_status, recordStatus: rec.record_status, rating, notRated, pipStatus: plan?.status || null };
    const decision = decide(base, pipRatings, own);
    const hide = own && decision === APPRAISAL_DECISION.AWAITING_RELEASE;
    const name = info.employee_name || nameOf(rec.employee_id) || rec.employee_id;
    rememberName(rec.employee_id, name);
    return {
      id: rec.performance_record_id,
      recordId: rec.performance_record_id,
      employeeId: rec.employee_id,
      employeeName: name,
      departmentId: info.department_id === null || info.department_id === undefined ? "" : String(info.department_id),
      departmentName: info.department_name || "—",
      designationName: info.designation_name || "—",
      managerId: rec.review_manager_employee_id || "",
      managerName: rec.review_manager_employee_id ? nameOf(rec.review_manager_employee_id) : "—",
      goalScore: hide ? null : goal,
      kraScore: hide ? null : kra,
      finalScore: hide ? null : final,
      rating: hide ? null : rating,
      recordStatus: rec.record_status,
      resultLabel: RESULT_LABEL[rec.record_status] || rec.record_status,
      stage: rec.current_stage,
      stageLabel: STAGE_LABEL[rec.current_stage] || rec.current_stage,
      eligibility: rec.eligibility_status,
      exclusionReason: "",
      releasedAt,
      acknowledgedAt: null,
      pipPlanId: own && plan?.status === "DRAFT" ? null : plan?.pip_plan_id || null,
      pipStatus: own && plan?.status === "DRAFT" ? null : plan?.status || null,
      decision,
      decisionLabel: DECISION_LABEL[decision],
    };
  }, 6, null)).filter(Boolean).sort((a, b) => a.employeeName.localeCompare(b.employeeName));

  // Same rule as POST /performance-cycles/{id}/close (cycles.py): every eligible appraisal must be released,
  // or acknowledged when the cycle's workflow requires acknowledgment.
  const workflow = cycle?.workflow || {};
  const ackRequired = Boolean(workflow.acknowledgment_required);
  const done = ackRequired ? ["ACKNOWLEDGED"] : ["RELEASED", "ACKNOWLEDGED"];
  const blockers = rows.filter((r) => r.eligibility === "ELIGIBLE" && !done.includes(r.recordStatus))
    .map((r) => ({ employeeId: r.employeeId, name: r.employeeName, stage: r.stageLabel, status: r.recordStatus }));
  const stages = Object.fromEntries(Object.keys(STAGE_LABEL).map((k) => [k, rows.filter((r) => r.eligibility === "ELIGIBLE" && r.stage === k).length]));
  const windows = Object.fromEntries((cycle?.stages || []).map((s) => [s.backendStage, [s.startAt, s.endAt]]));

  return ok({
    cycleName: cycle?.name,
    rule: { known: ruleKnown, enabled: ruleKnown && pipRatings.length > 0, bands: bandsCount, pipRatings },
    cycle: cycle ? {
      status: cycle.backendStatus,
      statusLabel: CYCLE_STATUS_LABEL[cycle.backendStatus] || cycle.status,
      performanceStart: cycle.startDate,
      performanceEnd: cycle.endDate,
      launchedAt: cycle.launchedAt,
      closedAt: cycle.closedAt,
      windows,
      releaseMode: workflow.release_mode,
      acknowledgmentRequired: ackRequired,
      closeBlockers: hr ? blockers : null,
      canClose: hr ? (!blockers.length && !["DRAFT", "CLOSED", "CANCELLED"].includes(cycle.backendStatus)) : null,
    } : null,
    stages,
    rows,
  });
}
